import { useEffect, useRef, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { TableHeadColFilterUntracked } from './styledComponents';
import { mixpanelTrack } from 'utils/app';
import { removeDiacritics } from 'utils/helpers';
import styled from 'styled-components';

const Wrapper = styled(TableHeadColFilterUntracked)`
    flex-grow: 1;
    flex-shrink: 1;
    min-width: ${({ $minWidth }) => $minWidth}rem;
    flex-basis: ${({ $width }) => $width};
    display: flex;
    align-items: stretch;
    padding: 0px;
    position: sticky;
    left: 0;
    z-index: 1;
    && {
        padding-left: 0px;
    }
    && > :first-child {
        margin-left: 0;
    }
    && > :last-child {
        margin-right: 0;
    }
`;

const IconWrapper = styled.span`
    position: relative;
    align-self: center;
`;

const Icon = styled(FiSearch)`
    position: absolute;
    left: ${({ theme }) => theme.space_6}px;
    transform: translateY(-50%);
    color: ${({ theme }) => theme.purple_1};
    font-size: 1.25em;
`;

const ICON_WIDTH = 42;

const Input = styled.input`
    font: inherit;
    font-weight: ${({ theme }) => theme.fontMedium};
    background: transparent;
    border: 0;
    flex-grow: 1;
    padding: ${({ theme }) => `0 ${theme.space_6}px 0 ${ICON_WIDTH}px`};
    text-transform: capitalize;
    position: relative;
    max-width: 100%;

    &:focus,
    &:hover {
        outline: ${({ theme }) => theme.purple_4} solid 1px;
        outline-offset: 0px;
    }
`;

const SearchMessage = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: ${({ theme }) => theme.gray_1};
    color: ${({ theme }) => theme.gray_5};
    padding: 0.5rem;
    font-size: 0.875rem;
    border: 1px solid ${({ theme }) => theme.gray_3};
    border-top: none;
    border-radius: 0 0 4px 4px;
    z-index: 10;
`;

const TERM_SPLIT_RE = /[,\s]+/;
const SEP_TRAILING_RE = /[,\s]$/;

function TableHeadColSearch({
    onSearchInputChange,
    name,
    limitSearchWords,
    showSearchMessage,
    ...props
}) {
    const [value, setValue] = useState('');
    const [showLimitMessage, setShowLimitMessage] = useState(false);
    const messageRef = useRef(null);

    const countWords = (s) => {
        const t = s.trim();
        return t ? t.split(TERM_SPLIT_RE).filter(Boolean).length : 0;
    };

    useEffect(() => {
        const cleanValue = removeDiacritics(value.trim());
        onSearchInputChange(cleanValue);
        // Track search input usage for comparison with SmartSearch
        if (cleanValue) {
            mixpanelTrack('Table Column Search', {
                query: cleanValue,
            });
        }
    }, [value, onSearchInputChange]);

    useEffect(() => {
        if (!showLimitMessage) return;
        const t = setTimeout(() => setShowLimitMessage(false), 4000);
        return () => clearTimeout(t);
    }, [showLimitMessage]);

    useEffect(() => {
        if (!showLimitMessage) return;
        const hide = (e) => {
            if (!messageRef.current) return;
            if (!messageRef.current.contains(e.target))
                setShowLimitMessage(false);
        };
        const onKey = (e) => {
            if (e.key === 'Escape') setShowLimitMessage(false);
        };
        // capture phase so it still fires if something stops propagation
        document.addEventListener('pointerdown', hide, true);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('pointerdown', hide, true);
            document.removeEventListener('keydown', onKey);
        };
    }, [showLimitMessage]);

    const wordCount = countWords(value);

    const handleChange = (e) => {
        const newValue = e.target.value;
        const prevCount = wordCount;
        const nextCount = countWords(newValue);

        const isDeletion = newValue.length < value.length;

        // block adding a new word when already at the limit
        const addingTrailingSpaceAtLimit =
            !!limitSearchWords &&
            prevCount >= limitSearchWords &&
            newValue.length > value.length &&
            SEP_TRAILING_RE.test(newValue);

        if (
            !limitSearchWords ||
            isDeletion ||
            (nextCount <= limitSearchWords && !addingTrailingSpaceAtLimit)
        ) {
            setValue(newValue);
            return;
        }

        if (showSearchMessage) setShowLimitMessage(true);
    };

    const placeholder = `Search in ${name || 'category'}`;

    return (
        <Wrapper {...props}>
            <IconWrapper>
                <Icon />
            </IconWrapper>
            <Input
                aria-label={placeholder}
                placeholder={placeholder}
                type="search"
                value={value}
                onChange={handleChange}
            />
            {showLimitMessage && (
                <SearchMessage ref={messageRef}>
                    Search limited to {limitSearchWords} words for performance.
                    Try a shorter search.
                </SearchMessage>
            )}
        </Wrapper>
    );
}

export default TableHeadColSearch;
